// /oauth/consent: an MCP client (or the desktop app) asks to act for the
// person. Shows who asks, where the access goes (the redirect's host) and
// what it could do; the answer goes back to the client. Anyone may register
// a client under any name, so the page says the app is not verified. Which
// Workspaces an app reaches comes with the Workspace parts.
import { useQuery } from "@tanstack/react-query";
import { m } from "@trameria/cloud-i18n";
import { apiErrorMessage, Button, ErrorLine, useApi } from "@trameria/ui-core";
import { signedOAuthQuery } from "../auth/oauth-query";
import { useCloud } from "../context";
import { oauthClientQuery, useOAuthConsent } from "../queries";
import { SignedOutPage } from "./signed-out-page";

/** What each scope lets the app do. An unknown scope is shown as it is. */
const SCOPES: Record<string, () => string> = {
  "trameria:read": () => m.web_oauth_scope_read(),
  "trameria:write": () => m.web_oauth_scope_write(),
  offline_access: () => m.web_oauth_scope_offline(),
  "trameria:desktop": () => m.web_oauth_scope_desktop(),
};

export function ConsentPage() {
  const { bootSearch } = useCloud();
  const params = new URLSearchParams(bootSearch);
  const clientId = params.get("client_id") ?? "";
  const scope = params.get("scope") ?? "";
  const oauthQuery = signedOAuthQuery(bootSearch);
  if (!oauthQuery || !clientId)
    return (
      <SignedOutPage title={m.web_oauth_title()}>
        <p className="text-[13px] leading-[1.7]">
          {m.web_oauth_outside_flow()}
        </p>
      </SignedOutPage>
    );
  return (
    <Consent
      clientId={clientId}
      scope={scope}
      oauthQuery={oauthQuery}
      destination={destinationOf(params.get("redirect_uri"))}
    />
  );
}

const LOOPBACK = new Set(["127.0.0.1", "[::1]", "localhost"]);

/**
 * Where the answer goes, as people can check it: the host of a web address,
 * an app on this device for a loopback one, or an app's own scheme. The
 * address is one the client registered (the server checks it before this
 * page opens, and the query is signed).
 */
export function destinationOf(redirect: string | null): string | null {
  if (!redirect) return null;
  let url: URL;
  try {
    url = new URL(redirect);
  } catch {
    return null;
  }
  if (url.protocol === "https:" || url.protocol === "http:")
    return LOOPBACK.has(url.hostname)
      ? m.web_oauth_this_device({ where: url.host })
      : url.host;
  return url.protocol;
}

function Consent({
  clientId,
  scope,
  oauthQuery,
  destination,
}: {
  clientId: string;
  scope: string;
  oauthQuery: string;
  destination: string | null;
}) {
  const api = useApi();
  const client = useQuery(oauthClientQuery(api, clientId));
  const consent = useOAuthConsent();
  const scopes = scope.split(" ").filter(Boolean);
  const desktop = scopes.includes("trameria:desktop");
  const decide = (accept: boolean) =>
    consent.mutate(
      { accept, scope, oauthQuery },
      { onSuccess: (url) => window.location.assign(url) },
    );
  return (
    <SignedOutPage title={m.web_oauth_title()}>
      <p className="text-[13px] leading-[1.7]">
        {m.web_oauth_connecting({
          client: client.data?.client_name ?? clientId,
        })}
      </p>
      {destination && (
        <p className="mt-2 break-all text-[13px] leading-[1.7]">
          {m.web_oauth_destination({ where: destination })}
        </p>
      )}
      <p className="mt-3 text-[12px] leading-[1.6] text-muted">
        {m.web_oauth_unverified()}
      </p>
      {desktop && (
        <p role="alert" className="mt-4 text-[13px] leading-[1.7] text-danger">
          {m.web_oauth_desktop_warning()}
        </p>
      )}
      <div className="mt-5">
        <div className="text-[12px] text-muted">{m.web_oauth_can_do()}</div>
        <ul className="mt-1.5 flex flex-col gap-1.5 text-[13px] leading-[1.6]">
          {scopes.map((s) => (
            <li key={s}>{SCOPES[s]?.() ?? s}</li>
          ))}
        </ul>
      </div>
      <div className="mt-6 flex gap-2">
        <Button
          variant="primary"
          disabled={consent.isPending}
          onClick={() => decide(true)}
        >
          {m.web_oauth_allow()}
        </Button>
        <Button disabled={consent.isPending} onClick={() => decide(false)}>
          {m.web_oauth_deny()}
        </Button>
      </div>
      {consent.isError && (
        <ErrorLine>
          {apiErrorMessage(consent.error, m.web_oauth_failed())}
        </ErrorLine>
      )}
    </SignedOutPage>
  );
}
